// src/components/sections/WhyChooseUs.js
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaUserFriends, FaShieldAlt, FaHeart, FaHandsHelping,
    FaStar, FaUserCheck, FaHospital, FaUsers
} from 'react-icons/fa';

const WhyChooseUs = () => {
    const reasons = [
        {
            icon: <FaUserFriends />,
            title: "Patient-First Approach",
            description: "Every decision is made with your health and comfort as our top priority",
            color: "#20B2AA"
        },
        {
            icon: <FaShieldAlt />,
            title: "Advanced Infrastructure",
            description: "State-of-the-art medical equipment and modern facilities",
            color: "#87CEEB"
        },
        {
            icon: <FaHeart />,
            title: "Personalized Care Plans",
            description: "Customized treatment plans tailored to individual needs",
            color: "#FF7F50"
        },
        {
            icon: <FaHandsHelping />,
            title: "Ethical & Transparent",
            description: "Clear communication and ethical treatment practices",
            color: "#9370DB"
        },
        {
            icon: <FaStar />,
            title: "Safe Motherhood Philosophy",
            description: "Comprehensive safety protocols for mothers and babies",
            color: "#FFD700"
        },
        {
            icon: <FaUserCheck />,
            title: "Child-Centric Care",
            description: "Specialized pediatric care in child-friendly environments",
            color: "#3CB371"
        },
        {
            icon: <FaHospital />,
            title: "Multi-disciplinary Teams",
            description: "Collaborative approach with specialists across departments",
            color: "#DC143C"
        },
        {
            icon: <FaUsers />,
            title: "Continuity of Care",
            description: "Seamless care from consultation through follow-up",
            color: "#FF6347"
        }
    ];

    return (
        <section id="why-choose-us" className="section-padding" style={{ backgroundColor: 'var(--light-bg)' }}>
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Why Choose Our Healthcare Center?
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Discover what makes us different and why families trust us with their healthcare needs
                    </p>
                </motion.div>

                <Row className="g-4">
                    {reasons.map((reason, index) => (
                        <Col lg={3} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                className="text-center p-4 h-100 healthcare-card"
                                style={{
                                    borderRadius: '15px',
                                    transition: 'all 0.3s ease'
                                }}
                                whileHover={{
                                    y: -10,
                                    scale: 1.02,
                                    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <motion.div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                    style={{
                                        width: '90px',
                                        height: '90px',
                                        backgroundColor: `${reason.color}15`,
                                        color: reason.color,
                                        fontSize: '2.2rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    animate={{
                                        y: [0, -10, 0]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: index * 0.2
                                    }}
                                    whileHover={{
                                        scale: 1.15,
                                        backgroundColor: `${reason.color}25`,
                                        boxShadow: `0 10px 20px ${reason.color}30`
                                    }}
                                >
                                    {reason.icon}
                                </motion.div>
                                <h5 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                    {reason.title}
                                </h5>
                                <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                    {reason.description}
                                </p>

                                {/* Subtle indicator line */}
                                <motion.div
                                    className="mx-auto"
                                    style={{
                                        width: '40px',
                                        height: '3px',
                                        backgroundColor: reason.color,
                                        borderRadius: '2px',
                                        marginTop: '15px',
                                        opacity: 0.5
                                    }}
                                    whileHover={{
                                        width: '60px',
                                        opacity: 1
                                    }}
                                    transition={{ duration: 0.3 }}
                                />
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-5 pt-5"
                >
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-4 mb-lg-0">
                            <motion.div
                                className="p-5 rounded h-100 healthcare-card"
                                style={{ backgroundColor: 'white' }}
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.3 }}
                            >
                                <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                                    Our Commitment to Excellence
                                </h4>
                                <p className="mb-4" style={{ color: '#666', lineHeight: '1.6' }}>
                                    We believe that exceptional healthcare goes beyond medical treatment.
                                    It's about compassion, understanding, and creating an environment where
                                    patients feel supported and cared for throughout their healthcare journey.
                                </p>
                                <div className="d-flex align-items-center justify-content-between">
                                    <motion.div
                                        className="text-center p-3 rounded"
                                        style={{
                                            backgroundColor: `${reasons[0].color}10`,
                                            flex: 1,
                                            margin: '0 8px'
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <h3 className="fw-bold mb-2" style={{ color: 'var(--primary-teal)' }}>98%</h3>
                                        <small style={{ color: '#666', fontSize: '0.85rem' }}>Patient Satisfaction</small>
                                    </motion.div>
                                    <motion.div
                                        className="text-center p-3 rounded"
                                        style={{
                                            backgroundColor: `${reasons[1].color}10`,
                                            flex: 1,
                                            margin: '0 8px'
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <h3 className="fw-bold mb-2" style={{ color: 'var(--primary-teal)' }}>99.5%</h3>
                                        <small style={{ color: '#666', fontSize: '0.85rem' }}>Safety Record</small>
                                    </motion.div>
                                    <motion.div
                                        className="text-center p-3 rounded"
                                        style={{
                                            backgroundColor: `${reasons[2].color}10`,
                                            flex: 1,
                                            margin: '0 8px'
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <h3 className="fw-bold mb-2" style={{ color: 'var(--primary-teal)' }}>24/7</h3>
                                        <small style={{ color: '#666', fontSize: '0.85rem' }}>Support Available</small>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </Col>

                        <Col lg={6}>
                            <motion.div
                                className="p-5 rounded h-100"
                                style={{
                                    background: 'linear-gradient(135deg, var(--primary-teal) 0%, var(--dark-teal) 100%)',
                                    color: 'white'
                                }}
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.3 }}
                            >
                                <h4 className="fw-bold mb-4">Quality Assurance</h4>
                                <ul className="list-unstyled">
                                    {[
                                        "Regular quality audits and accreditation compliance",
                                        "Continuous staff training and development programs",
                                        "Patient feedback integration for service improvement",
                                        "Transparent pricing and ethical billing practices"
                                    ].map((item, idx) => (
                                        <motion.li
                                            key={idx}
                                            className="mb-3 d-flex align-items-start"
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                                            viewport={{ once: true }}
                                            whileHover={{ x: 5 }}
                                        >
                                            <FaStar className="me-3 mt-1" style={{ color: '#FFD700' }} />
                                            <span style={{ fontSize: '0.95rem' }}>{item}</span>
                                        </motion.li>
                                    ))}
                                </ul>

                                {/* Quality badge */}
                                <motion.div
                                    className="text-center mt-4 pt-3 border-top border-white border-opacity-25"
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ duration: 0.8, delay: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                            backdropFilter: 'blur(5px)'
                                        }}
                                    >
                                        <FaStar className="me-2" style={{ color: '#FFD700', fontSize: '0.9rem' }} />
                                        <span style={{ fontSize: '0.85rem' }}>JCI Accredited Healthcare Facility</span>
                                    </div>
                                </motion.div>
                            </motion.div>
                        </Col>
                    </Row>
                </motion.div>
            </Container>
        </section>
    );
};

export default WhyChooseUs;